import React, {useCallback} from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {mb, mr, mt, p} from "@pg-design/helpers-css";
import {LikeIcon, LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";

import {getPropertyPlanAltText} from "../../comparison_tool/utils/get_property_plan_modal_title";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {isPropertyWithDiscountPrice} from "../../property/utils/is_property_with_discount_price";
import {PropertyListApplicationButton} from "../../property_list/components/property_list_application_button/PropertyListApplicationButton";
import {getCurrency} from "../../utils/get_currency";
import {OfferBoxBase} from "../OfferBoxBase";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
}

export const PropertyBox = (props: IProps) => {
    const {property} = props;
    const {offer} = property;
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const theme = useTheme();
    const externalPlanUrl = property?.external_plan_url;

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_375x250;
    const altText = getPropertyPlanAltText(offer.name, property);

    const hasDiscountPrice = isPropertyWithDiscountPrice(props.property);
    const forceShowPrice = property.offer.vendor.configuration.force_show_price;

    const onPropertyBoxClick = useCallback(() => {
        props.onClick && props.onClick(property);
    }, [property]);

    //TODO: Add source for isRecommended, same problem in PropertyCard.tsx from CU-862j65nv5
    const isRecommended = false;
    const isReserved = property.is_reserved;
    const isPromotion = !!property.discount && property.discount > 0;

    const Component = props.as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} css={[p(2), propertyBox]}>
            <Component href={propertyLink} css={propertyBoxLink} title={offer.name} target={target}>
                <div css={badgeWrapper}>
                    {isReserved && (
                        <Badge variant="label_danger" icon={() => <LockOutlineIcon size="1.6" fill="white" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && isRecommended && (
                        <Badge variant="label_success" icon={() => <LikeIcon size="1.6" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rekomendowane
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && !isRecommended && isPromotion && (
                        <Badge variant="label_info" icon={() => <PercentIcon size="1.6" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                    {/* Due to CU-8693ecg7r temporary commented - program "Kredyt 2%" has ended at the begging of 2024 */}
                    {/*{property.credit_two_percent && (*/}
                    {/*    <Badge variant="label_highlight" icon={() => <MegaphoneIcon size="1.6" css={mr()} />}>*/}
                    {/*        <Text variant="info_txt_2" as="span" strong>*/}
                    {/*            Kredyt 2%*/}
                    {/*        </Text>*/}
                    {/*    </Badge>*/}
                    {/*)}*/}
                </div>

                <CenteredImage
                    css={planWrap}
                    src={planImage}
                    alt={altText}
                    width={375}
                    height={250}
                    breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                />

                <div css={[mt(3), mb(2), contentWrap]}>
                    <ul css={[propertyBoxInfoList]}>
                        <li css={propertyBoxInfoListItem}>
                            <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text css={textStyle} variant="info_txt_1" as="span">
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text css={textStyle} variant="info_txt_1" as="span">
                                {property.rooms}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text css={textStyle} variant="info_txt_1" as="span">
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {(hasDiscountPrice || (forceShowPrice && property.price > 0)) && (
                            <li css={propertyBoxInfoListItem}>
                                <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text css={textStyle} variant="info_txt_1" as="span">
                                    {priceFormat(property.price, {unit: getCurrency({currency: offer.currency})})}
                                </Text>
                            </li>
                        )}
                    </ul>
                    <div css={infoWrap}>
                        {hasDiscountPrice && (
                            <Text variant="info_txt_3" color={theme.colors.gray[700]} align="center" css={p(1, 0, 0.5, 0)}>
                                Najniższa cena z ostatnich 30 dni, przed obniżką: <strong>{priceFormat(property.lowest_price_within_30_days as number)}</strong>
                            </Text>
                        )}
                    </div>
                </div>
                <div css={propertyButton}>
                    <PropertyListApplicationButton
                        property={property}
                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                        vendorId={offer.vendor.id}
                        isPlansList
                    />
                </div>
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
export const propertyBoxLink = css`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const propertyBox = css`
    height: 100%;
`;

const planWrap = css`
    flex: 0 0 auto;

    & img {
        object-fit: contain;
        width: 100% !important; // override CenteredImage styles to fit image to container
        height: 100% !important; // override CenteredImage styles to fit image to container
    }
`;

const contentWrap = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const infoWrap = css`
    flex: 1 1 auto;
`;

const propertyButton = css`
    flex: 0 1 auto;
`;

const propertyBoxInfoList = css`
    padding-left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const propertyBoxInfoListItem = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const textStyle = (theme: Theme) => css`
    font-weight: 500;
    font-size: 1.2rem;

    @media (min-width: ${theme.breakpoints.md}) {
        font-size: 1.2rem;
    }
`;
const lightColor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
`;

const badgeWrapper = css`
    position: absolute;
    z-index: 30;
    display: flex;
    gap: 1rem;
`;
import React, {useCallback} from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {mb, mr, mt, p} from "@pg-design/helpers-css";
import {LikeIcon, LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";

import {getPropertyPlanAltText} from "../../comparison_tool/utils/get_property_plan_modal_title";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {isPropertyWithDiscountPrice} from "../../property/utils/is_property_with_discount_price";
import {PropertyListApplicationButton} from "../../property_list/components/property_list_application_button/PropertyListApplicationButton";
import {getCurrency} from "../../utils/get_currency";
import {OfferBoxBase} from "../OfferBoxBase";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
}

export const PropertyBox = (props: IProps) => {
    const {property} = props;
    const {offer} = property;
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const theme = useTheme();
    const externalPlanUrl = property?.external_plan_url;

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_375x250;
    const altText = getPropertyPlanAltText(offer.name, property);

    const hasDiscountPrice = isPropertyWithDiscountPrice(props.property);
    const forceShowPrice = property.offer.vendor.configuration.force_show_price;

    const onPropertyBoxClick = useCallback(() => {
        props.onClick && props.onClick(property);
    }, [property]);

    //TODO: Add source for isRecommended, same problem in PropertyCard.tsx from CU-862j65nv5
    const isRecommended = false;
    const isReserved = property.is_reserved;
    const isPromotion = !!property.discount && property.discount > 0;

    const Component = props.as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} css={[p(2), propertyBox]}>
            <Component href={propertyLink} css={propertyBoxLink} title={offer.name} target={target}>
                <div css={badgeWrapper}>
                    {isReserved && (
                        <Badge variant="label_danger" icon={() => <LockOutlineIcon size="1.6" fill="white" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && isRecommended && (
                        <Badge variant="label_success" icon={() => <LikeIcon size="1.6" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rekomendowane
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && !isRecommended && isPromotion && (
                        <Badge variant="label_info" icon={() => <PercentIcon size="1.6" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                    {/* Due to CU-8693ecg7r temporary commented - program "Kredyt 2%" has ended at the begging of 2024 */}
                    {/*{property.credit_two_percent && (*/}
                    {/*    <Badge variant="label_highlight" icon={() => <MegaphoneIcon size="1.6" css={mr()} />}>*/}
                    {/*        <Text variant="info_txt_2" as="span" strong>*/}
                    {/*            Kredyt 2%*/}
                    {/*        </Text>*/}
                    {/*    </Badge>*/}
                    {/*)}*/}
                </div>

                <CenteredImage
                    css={planWrap}
                    src={planImage}
                    alt={altText}
                    width={375}
                    height={250}
                    breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                />

                <div css={[mt(3), mb(2), contentWrap]}>
                    <ul css={[propertyBoxInfoList]}>
                        <li css={propertyBoxInfoListItem}>
                            <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text css={textStyle} variant="info_txt_1" as="span">
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text css={textStyle} variant="info_txt_1" as="span">
                                {property.rooms}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text css={textStyle} variant="info_txt_1" as="span">
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {(hasDiscountPrice || (forceShowPrice && property.price > 0)) && (
                            <li css={propertyBoxInfoListItem}>
                                <Text css={[textStyle, lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text css={textStyle} variant="info_txt_1" as="span">
                                    {priceFormat(property.price, {unit: getCurrency({currency: offer.currency})})}
                                </Text>
                            </li>
                        )}
                    </ul>
                    <div css={infoWrap}>
                        {hasDiscountPrice && (
                            <Text variant="info_txt_3" color={theme.colors.gray[700]} align="center" css={p(1, 0, 0.5, 0)}>
                                Najniższa cena z ostatnich 30 dni, przed obniżką: <strong>{priceFormat(property.lowest_price_within_30_days as number)}</strong>
                            </Text>
                        )}
                    </div>
                </div>
                <div css={propertyButton}>
                    <PropertyListApplicationButton
                        property={property}
                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                        vendorId={offer.vendor.id}
                        isPlansList
                    />
                </div>
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
export const propertyBoxLink = css`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const propertyBox = css`
    height: 100%;
`;

const planWrap = css`
    flex: 0 0 auto;

    & img {
        object-fit: contain;
        width: 100% !important; // override CenteredImage styles to fit image to container
        height: 100% !important; // override CenteredImage styles to fit image to container
    }
`;

const contentWrap = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const infoWrap = css`
    flex: 1 1 auto;
`;

const propertyButton = css`
    flex: 0 1 auto;
`;

const propertyBoxInfoList = css`
    padding-left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const propertyBoxInfoListItem = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const textStyle = (theme: Theme) => css`
    font-weight: 500;
    font-size: 1.2rem;

    @media (min-width: ${theme.breakpoints.md}) {
        font-size: 1.2rem;
    }
`;
const lightColor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
`;

const badgeWrapper = css`
    position: absolute;
    z-index: 30;
    display: flex;
    gap: 1rem;
`;
